import {
  defaultOrganisation,
  testOrganisationResourcesDisabled,
  testOrganisationWithFeatureFlag,
} from "./organisation";
import {
  GetOrganisationDetailsDocument,
  GetOrganisationDetailsQuery,
  GetOrganisationDocument,
  GetOrganisationQuery,
  GetOrganisationQueryVariables,
} from "../GeneratedGraphQL";

import { defaultService } from "./service";
import { MockedResponse } from "@apollo/client/testing";
import { multipleServices } from "./service";

const organisation = {
  ...defaultOrganisation,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: {
      endCursor: null,
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      total: 1,
    },
  },
};

const organisationResourcesDisabled = {
  ...testOrganisationResourcesDisabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: {
      endCursor: defaultService._id,
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: defaultService._id,
      total: 1,
    },
  },
};

export const defaultOrganisationRequest: MockedResponse<GetOrganisationQuery> =
  {
    request: {
      query: GetOrganisationDocument,
      variables: { id: defaultOrganisation._id },
    },
    result: { data: { organisation: organisation } },
  };

export const genericMultiPageServicesOrganisationRequests: MockedResponse<GetOrganisationQuery>[] =
  multipleServices.map((service, i) => {
    const variables: GetOrganisationQueryVariables = {
      id: defaultOrganisation._id,
    };
    if (i !== 0) {
      variables.serviceCursor = multipleServices[i - 1]._id;
    }

    return {
      request: { query: GetOrganisationDocument, variables },
      result: {
        data: {
          organisation: {
            ...organisation,
            services: {
              edges: [{ cursor: service._id, node: service }],
              /* eslint-disable sort-keys */
              pageInfo: {
                hasPreviousPage: false,
                hasNextPage: true,
                total: multipleServices.length,
                startCursor: service._id,
                endCursor: service._id,
              },
              /* eslint-enable sort-keys */
            },
          },
        },
      },
    };
  });

export const organisationForceTimezoneRequest: MockedResponse<GetOrganisationQuery> =
  {
    request: {
      query: GetOrganisationDocument,
      variables: { id: testOrganisationResourcesDisabled._id },
    },
    result: {
      data: {
        organisation: {
          ...organisationResourcesDisabled,
          forceTimezone: "Europe/Berlin",
        },
      },
    },
  };

/**
 * mock for BookingToolData's scoped-organisation fast path
 * (ScopedBookingToolData), which fetches organisation *details* via the
 * slim `GetOrganisationDetails` query — not `GetOrganisation` — since its
 * service comes from the booking-tool-scoped `GetBookingToolSettings`
 * query instead (see BookingToolData.tsx and mocks/bookingTool.ts for why:
 * Organisation.services has no awareness of booking tool category/resource
 * restrictions, so it must not be used to pick the service for a specific
 * booking tool). `GetOrganisationDetails` takes only `id` — no
 * serviceFilters/serviceCursor — so there's no `services` field on the
 * result below.
 */
export const organisationDetailsWithFeatureFlagRequest: MockedResponse<GetOrganisationDetailsQuery> =
  {
    request: {
      query: GetOrganisationDetailsDocument,
      variables: {
        id: testOrganisationWithFeatureFlag._id,
      },
    },
    result: {
      data: {
        organisation: {
          ...testOrganisationWithFeatureFlag,
          adyenIntegrationToken: null,
          brandConfig: null,
          customBookingNotesText: null,
          customContent: null,
          customNoAvailabilityText: null,
          /**
           * The shared bookingQuestions mock fixture doesn't populate
           * every field GetOrganisationDetails' externalBookingQuestions
           * selection asks for (e.g. `services`), which trips up Apollo's
           * cache normalization (silently resulting in `data: undefined`
           * rather than a visible error). Not relevant to what this fixture
           * is for, so simplified to an empty list here.
           */
          externalBookingQuestions: [],
          hideAppointeddFooter: null,
          marketingOptInLabel: null,
          privacyPolicyUrl: null,
          squareIntegration: null,
        },
      },
    },
  };
